import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Plane, TrendingUp } from 'lucide-react';

export default function AircraftProductionChart() {
  const [animatedData, setAnimatedData] = useState([]);
  const [hoveredBarKey, setHoveredBarKey] = useState(null); // `${index}-commercial|executive`
  const [tooltipData, setTooltipData] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  // Refs
  const scrollRef = useRef(null);
  const innerRefDesktop = useRef(null);
  const innerRefMobile  = useRef(null);
  const tooltipRef      = useRef(null);

  // Tamanho real do tooltip (medido após render)
  const [tooltipSize, setTooltipSize] = useState({ w: 140, h: 44 });

  // Espaços extras no mobile (dinâmicos)
  const [extraTop, setExtraTop] = useState(56); // espaço para caber o tooltip acima das barras
  const [gutter, setGutter]     = useState(24); // padding lateral p/ evitar clamp horizontal

  const productionData = [
    { year: 2010, commercial: 101, executive: 144 }, { year: 2011, commercial: 105, executive: 99 },
    { year: 2012, commercial: 106, executive: 99 },  { year: 2013, commercial: 90,  executive: 119 },
    { year: 2014, commercial: 92,  executive: 116 }, { year: 2015, commercial: 101, executive: 120 },
    { year: 2016, commercial: 108, executive: 117 }, { year: 2017, commercial: 101, executive: 109 },
    { year: 2018, commercial: 90,  executive: 91 },  { year: 2019, commercial: 89,  executive: 109 },
    { year: 2020, commercial: 44,  executive: 86 },  { year: 2021, commercial: 48,  executive: 93 },
    { year: 2022, commercial: 57,  executive: 102 }, { year: 2023, commercial: 64,  executive: 115 },
    { year: 2024, commercial: 73,  executive: 130 }
  ];

  const CHART_HEIGHT = 320; // px
  const maxValue = Math.max(...productionData.map(d => Math.max(d.commercial, d.executive)));

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setAnimatedData(productionData), 400);
    return () => clearTimeout(timer);
  }, []);

  // Ajusta espaços extras no mobile conforme o tamanho real do tooltip
  useEffect(() => {
    if (!isMobile) return;
    // Espaço de topo = altura do tooltip + margem
    setExtraTop(Math.max(56, tooltipSize.h + 12));
    // Gutter lateral = metade do tooltip + uma folga
    setGutter(Math.max(24, Math.ceil(tooltipSize.w / 2) + 8));
  }, [tooltipSize, isMobile]);

  const getBarHeight = (value) => (value / maxValue) * (CHART_HEIGHT - 40);

  const clsComm = (isHovered) =>
    `rounded-t transition-all duration-300 cursor-pointer ${isHovered ? 'bg-sky-600' : 'bg-sky-500'}`;
  const clsExec = (isHovered) =>
    `rounded-t transition-all duration-300 cursor-pointer ${isHovered ? 'bg-indigo-600' : 'bg-indigo-500'}`;

  function positionTooltip(item, key, e) {
    const activeInnerRef = isMobile ? innerRefMobile : innerRefDesktop;
    if (!activeInnerRef.current) return;

    const barRect   = e.currentTarget.getBoundingClientRect();
    const innerRect = activeInnerRef.current.getBoundingClientRect();

    // posição base relativa ao container
    let baseX = (barRect.left - innerRect.left) + (barRect.width / 2);
    let baseY = (barRect.top  - innerRect.top)  - 10;

    // Clamp leve (segurança) — com gutter e espaço extra quase não atuará
    if (isMobile) {
      const PADDING = 8;
      const tw = tooltipSize.w;
      const th = tooltipSize.h;

      // Horizontal (translateX(-50%)): centro deve caber entre as bordas internas
      const minX = PADDING + (tw / 2);
      const maxX = innerRect.width - PADDING - (tw / 2);
      baseX = Math.max(minX, Math.min(baseX, maxX));

      // Vertical (translateY(-100%)): base do tooltip precisa permitir topo visível
      const minY = PADDING + th;
      baseY = Math.max(minY, baseY);
    }

    const type = key.split('-')[1]; // commercial|executive
    setHoveredBarKey(key);
    setTooltipData({
      year: item.year,
      value: item[type],
      type: type === 'commercial' ? 'Aeronaves Comerciais' : 'Aeronaves Executivas',
      x: baseX,
      y: baseY
    });
  }

  const onEnterOrTouch = (item, key) => (e) => positionTooltip(item, key, e);
  const onLeave = () => { setHoveredBarKey(null); setTooltipData(null); };

  // Re-medição do tooltip quando aparece no mobile e re-clamp se necessário
  useEffect(() => {
    if (!tooltipData || !tooltipRef.current || !isMobile || !innerRefMobile.current) return;

    const rect = tooltipRef.current.getBoundingClientRect();
    const innerRect = innerRefMobile.current.getBoundingClientRect();

    if (Math.abs(rect.width - tooltipSize.w) > 2 || Math.abs(rect.height - tooltipSize.h) > 2) {
      const PADDING = 8;
      const tw = rect.width;
      const th = rect.height;

      const minX = PADDING + tw / 2;
      const maxX = innerRect.width - PADDING - tw / 2;
      const minY = PADDING + th;

      setTooltipSize({ w: tw, h: th });

      setTooltipData(prev => prev ? {
        ...prev,
        x: Math.max(minX, Math.min(prev.x, maxX)),
        y: Math.max(minY, prev.y)
      } : prev);
    }
  }, [tooltipData, isMobile]);

  // --- Dimensões mobile (2 barras por ano) ---
  const BAR_W_MOBILE = 16;
  const GAP_BETWEEN_BARS = 6;    // entre comerciais e executivas
  const GAP_BETWEEN_GROUPS = 14; // entre anos
  const GROUP_W_MOBILE = (BAR_W_MOBILE * 2) + GAP_BETWEEN_BARS + GAP_BETWEEN_GROUPS;
  const baseWidthMobile = (productionData.length * GROUP_W_MOBILE);
  // Largura final: conteúdo + gutters laterais
  const contentWidthMobile = baseWidthMobile + gutter * 2;

  return (
    <div className="space-y-8">
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl font-bold text-center">Evolução da Produção de Aeronaves</CardTitle>
          <p className="text-center text-gray-600">Produção de aeronaves comerciais e executivas no Brasil (em unidades)</p>
        </CardHeader>
        <CardContent>
          {isMobile ? (
            // ----- MOBILE: scroll horizontal com área extra -----
            <div className="overflow-x-auto no-scrollbar" ref={scrollRef}>
              <div
                className="relative flex items-end"
                ref={innerRefMobile}
                style={{
                  height: CHART_HEIGHT + extraTop,  // mais altura total
                  width:  contentWidthMobile,       // gutter lateral incluso
                  paddingTop: extraTop,             // espaço real acima das barras
                  paddingLeft: gutter,
                  paddingRight: gutter
                }}
              >
                {productionData.map((item, index) => (
                  <div
                    key={item.year}
                    className="flex flex-col items-center justify-end"
                    style={{ width: GROUP_W_MOBILE }}
                  >
                    <div className="flex items-end" style={{ gap: GAP_BETWEEN_BARS }}>
                      {/* Comerciais */}
                      <div
                        className={`${clsComm(hoveredBarKey === `${index}-commercial`)}`}
                        style={{
                          width: BAR_W_MOBILE,
                          height: `${animatedData[index] ? getBarHeight(item.commercial) : 0}px`,
                          transitionDelay: `${index * 40}ms`,
                          transform: hoveredBarKey === `${index}-commercial` ? 'translateY(-2px)' : 'translateY(0)'
                        }}
                        onTouchStart={onEnterOrTouch(item, `${index}-commercial`)}
                        onMouseEnter={onEnterOrTouch(item, `${index}-commercial`)}
                        onMouseLeave={onLeave}
                      />
                      {/* Executivas */}
                      <div
                        className={`${clsExec(hoveredBarKey === `${index}-executive`)}`}
                        style={{
                          width: BAR_W_MOBILE,
                          height: `${animatedData[index] ? getBarHeight(item.executive) : 0}px`,
                          transitionDelay: `${index * 40 + 16}ms`,
                          transform: hoveredBarKey === `${index}-executive` ? 'translateY(-2px)' : 'translateY(0)'
                        }}
                        onTouchStart={onEnterOrTouch(item, `${index}-executive`)}
                        onMouseEnter={onEnterOrTouch(item, `${index}-executive`)}
                        onMouseLeave={onLeave}
                      />
                    </div>
                    <span className="text-[10px] mt-1 text-gray-500">{String(item.year).slice(2)}</span>
                  </div>
                ))}

                {tooltipData && (
                  <div
                    ref={tooltipRef}
                    className="absolute z-10 bg-gray-900 text-white px-3 py-2 rounded-lg shadow-lg pointer-events-none transition-all duration-200 whitespace-nowrap"
                    style={{ left: tooltipData.x, top: tooltipData.y, transform: 'translate(-50%,-100%)' }}
                  >
                    <div className="text-sm font-semibold">{tooltipData.year}</div>
                    <div className="text-xs">{tooltipData.type}: {tooltipData.value} unidades</div>
              
                  </div>
                )}
              </div>
            </div>
          ) : (
            // ----- DESKTOP -----
            <div className="h-80 px-4 relative" ref={innerRefDesktop}>
              <div className="flex items-end justify-between h-full gap-2">
                {productionData.map((item, index) => (
                  <div key={item.year} className="flex flex-col items-center justify-end h-full">
                    <div className="flex items-end gap-1 h-full">
                      {/* Comerciais */}
                      <div
                        className={`w-4 ${clsComm(hoveredBarKey === `${index}-commercial`)} hover:opacity-90`}
                        style={{
                          height: `${animatedData[index] ? getBarHeight(item.commercial) : 0}px`,
                          transitionDelay: `${index * 50}ms`,
                          transform: hoveredBarKey === `${index}-commercial` ? 'translateY(-2px)' : 'translateY(0)'
                        }}
                        onMouseEnter={onEnterOrTouch(item, `${index}-commercial`)}
                        onMouseLeave={onLeave}
                      />
                      {/* Executivas */}
                      <div
                        className={`w-4 ${clsExec(hoveredBarKey === `${index}-executive`)} hover:opacity-90`}
                        style={{
                          height: `${animatedData[index] ? getBarHeight(item.executive) : 0}px`,
                          transitionDelay: `${index * 50 + 20}ms`,
                          transform: hoveredBarKey === `${index}-executive` ? 'translateY(-2px)' : 'translateY(0)'
                        }}
                        onMouseEnter={onEnterOrTouch(item, `${index}-executive`)}
                        onMouseLeave={onLeave}
                      />
                    </div>
                    <span className="text-xs mt-2 text-gray-500">{item.year}</span>
                  </div>
                ))}
              </div>

              {tooltipData && (
                <div
                  className="absolute z-10 bg-gray-900 text-white px-3 py-2 rounded-lg shadow-lg pointer-events-none transition-all duration-200"
                  style={{ left: tooltipData.x, top: tooltipData.y, transform: 'translate(-50%,-100%)' }}
                >
                  <div className="text-sm font-semibold">{tooltipData.year}</div>
                  <div className="text-xs">{tooltipData.type}: {tooltipData.value} unidades</div>

                </div>
              )}
            </div>
          )}

          {/* Legenda */}
          <div className="mt-6 flex justify-center gap-6 flex-wrap">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-sky-500 rounded"></div>
              <Plane className="w-4 h-4 mr-1" />
              <span>Aeronaves Comerciais</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-indigo-500 rounded"></div>
              <Plane className="w-4 h-4 mr-1" />
              <span>Aeronaves Executivas</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Análise Detalhada dos Resultados */}
      <div className="grid md:grid-cols-2 gap-6">
        <Card className="bg-gradient-to-br from-sky-50 to-blue-100">
          <CardHeader>
            <CardTitle className="text-xl font-bold text-sky-900 flex items-center gap-2">
              <Plane className="w-6 h-6" />
              Aeronaves Comerciais: Retomada Gradual
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-white/60 p-4 rounded-lg">
              <h4 className="font-semibold text-sky-800 mb-2">Impacto da Pandemia (2020-2021)</h4>
              <p className="text-sm text-gray-700">
                A produção despencou para apenas 44 unidades em 2020, o menor nível da série histórica,
                reflexo das restrições globais de viagens e cancelamentos de pedidos pelas companhias aéreas.
              </p>
            </div>
            <div className="bg-white/60 p-4 rounded-lg">
              <h4 className="font-semibold text-sky-800 mb-2">Recuperação Consistente (2024)</h4>
              <p className="text-sm text-gray-700">
                Com 73 unidades entregues em 2024, a Embraer demonstra recuperação sólida no segmento comercial,
                mantendo sua posição entre os quatro maiores fabricantes mundiais de aeronaves comerciais.
              </p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-indigo-50 to-purple-100">
          <CardHeader>
            <CardTitle className="text-xl font-bold text-indigo-900 flex items-center gap-2">
              <TrendingUp className="w-6 h-6" />
              Aeronaves Executivas: Crescimento Excepcional
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-white/60 p-4 rounded-lg">
              <h4 className="font-semibold text-indigo-800 mb-2">Forte Demanda Pós-Pandemia</h4>
              <p className="text-sm text-gray-700">
                O segmento executivo mostrou resiliência excepcional, com 130 unidades em 2024,
                impulsionado pela demanda por soluções de mobilidade aérea privada e viagens corporativas.
              </p>
            </div>
            <div className="bg-white/60 p-4 rounded-lg">
              <h4 className="font-semibold text-indigo-800 mb-2">Diversificação Estratégica</h4>
              <p className="text-sm text-gray-700">
                O investimento da Embraer na aviação executiva, com modelos como Phenom e Praetor,
                reduziu a dependência de jatos comerciais e fortaleceu a presença no mercado corporativo internacional.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Contexto Geral da Embraer */}
      <Card className="bg-gradient-to-r from-amber-50 to-orange-50">
        <CardHeader>
          <CardTitle className="text-xl font-bold text-amber-900">Contexto da Indústria Aeronáutica Brasileira</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-3 gap-4">
            <div className="bg-white/60 p-4 rounded-lg">
              <h4 className="font-semibold text-amber-800 mb-2">Liderança Mundial</h4>
              <p className="text-sm text-gray-700">
                A Embraer detém participação relevante no mercado de jatos de 61 a 120 assentos,
                com exportações representando a maior parte das vendas.
              </p>
            </div>
            <div className="bg-white/60 p-4 rounded-lg">
              <h4 className="font-semibold text-amber-800 mb-2">Impacto Econômico</h4>
              <p className="text-sm text-gray-700">
                O setor aeroespacial impulsiona exportações e gera empregos qualificados, fortalecendo a balança comercial.
              </p>
            </div>
            <div className="bg-white/60 p-4 rounded-lg">
              <h4 className="font-semibold text-amber-800 mb-2">Capacidade Produtiva</h4>
              <p className="text-sm text-gray-700">
                O Brasil mantém domínio sobre o ciclo produtivo aeronáutico, com capacidade para volumes anuais relevantes.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
